import { useEffect, useState } from "react";

/**
 * Live countdown to a given ISO timestamp.
 *
 * Tick cadence:
 *   - every second when delta ≤ 1 hour
 *   - every minute otherwise
 *
 * Render formats (delta > 0):
 *   - delta > 24h  → "Ouvre le 22 juin à 18:00"   (opening)
 *                  → "Ferme le 22 juin à 18:00"   (closing)
 *   - delta > 1h   → "Avant ouverture : 2h 14min" (opening)
 *                  → "Avant fermeture : 2h 14min" (closing)
 *   - delta ≤ 1h   → "Avant ouverture : 03:21:10" (opening)
 *                  → "Avant fermeture : 03:21:10" (closing)
 *   - delta ≤ 0    → renders nothing.
 *
 * Props:
 *   - target: ISO timestamp string (required)
 *   - kind: "opening" (default) | "closing"
 *   - className / dataTestid: passthrough
 */
export default function Countdown({
  target,
  kind = "opening",
  className = "",
  dataTestid = "countdown",
}) {
  const [now, setNow] = useState(() => Date.now());
  const targetMs = target ? new Date(target).getTime() : 0;
  const delta = Math.max(0, targetMs - now);

  useEffect(() => {
    if (!targetMs) return undefined;
    if (delta <= 0) return undefined;
    const tickMs = delta <= 60 * 60 * 1000 ? 1000 : 60 * 1000;
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [delta, targetMs]);

  if (!targetMs || delta <= 0) return null;

  const totalSec = Math.floor(delta / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");

  const prefix = kind === "closing" ? "Avant fermeture" : "Avant ouverture";
  const farVerb = kind === "closing" ? "Ferme" : "Ouvre";

  let label;
  if (delta > 24 * 60 * 60 * 1000) {
    const date = new Date(targetMs).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    label = `${farVerb} le ${date}`;
  } else if (delta > 60 * 60 * 1000) {
    label = `${prefix} : ${hours}h ${pad(minutes)}min`;
  } else {
    label = `${prefix} : ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return (
    <span
      data-testid={dataTestid}
      data-days={days}
      data-kind={kind}
      className={className}
    >
      {label}
    </span>
  );
}
